import { Link } from "react-router-dom";
import { BookOpen, MapPinned, Camera, CalendarCheck, Rocket } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";

const COMPARISONS = [
  {
    icon: BookOpen,
    testId: "compare-review-platforms",
    title: "Review & discovery platforms",
    oldWay:
      "Great for browsing ratings and reading what thousands of strangers think — but your own list of places to try ends up scattered, saved in a profile you rarely revisit.",
    craveWay:
      "Your own Saved and Visited log keeps every place you want to try — and every meal worth remembering — in one spot that's entirely yours.",
  },
  {
    icon: MapPinned,
    testId: "compare-map-apps",
    title: "Map apps & saved lists",
    oldWay:
      "Perfect for directions and seeing what's nearby right now. But saved pins pile up without context — why you saved them, or whether you ever went.",
    craveWay:
      "Save places with intention, mark them visited when you go, and build a personal dining history you can actually revisit and enjoy.",
  },
  {
    icon: Camera,
    testId: "compare-notes-screenshots",
    title: "Notes, screenshots & wishlists",
    oldWay:
      "A photo here, a note there, a screenshot buried in your camera roll. Finding that one restaurant later becomes a treasure hunt.",
    craveWay:
      "Everything about a place — saved, visited, remembered — lives in one dining companion instead of five different apps.",
  },
  {
    icon: CalendarCheck,
    testId: "compare-invitations",
    title: "Invitations & group coordination",
    oldWay:
      "Coordinating dinner over a group chat means long threads, lost polls, and plans that fizzle out before anyone picks a place.",
    craveWay:
      "When you want company, send one invitation with an optional vote. It's a supporting feature — Foodie Crave works beautifully solo.",
  },
];

export default function Compare() {
  return (
    <div className="min-h-screen overflow-x-clip bg-crave-bg font-sans text-crave-cream">
      <Seo
        title="Foodie Crave Compared | A Personal Dining Experience App"
        description="See how Foodie Crave compares to review platforms, map apps, and scattered notes for keeping track of where to eat — a personal dining experience app with restaurant discovery built in."
        path="/compare"
      />
      <Navbar />
      <main>
        <section className="relative overflow-hidden pt-32 pb-16 md:pt-44 md:pb-20" data-testid="compare-hero">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-48 -left-48 h-[36rem] w-[36rem] rounded-full bg-crave-red/25 blur-[140px]"
          />
          <div className="relative mx-auto max-w-3xl px-6 text-center md:px-12">
            <Reveal>
              <p className="text-xs font-bold uppercase tracking-[0.28em] text-crave-gold">Compare</p>
              <h1 className="mt-4 font-display text-4xl font-medium leading-[1.05] tracking-tight text-crave-cream sm:text-5xl md:text-6xl">
                More than finding a place to eat.
              </h1>
              <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-crave-muted">
                Foodie Crave helps you discover restaurants, save places you want to try, remember
                where you've eaten, and revisit favorites — all in one simple dining companion.
              </p>
            </Reveal>
          </div>
        </section>

        <section className="pb-24 md:pb-32" data-testid="compare-section">
          <div className="mx-auto max-w-5xl space-y-6 px-6 md:px-12">
            {COMPARISONS.map((c, i) => (
              <Reveal key={c.testId} delay={i * 0.08}>
                <div
                  data-testid={c.testId}
                  className="grid grid-cols-1 gap-6 rounded-3xl border border-crave-border bg-crave-card p-8 transition-colors duration-500 hover:border-crave-gold/40 md:grid-cols-2 md:p-10"
                >
                  <div>
                    <span className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-crave-red/25 text-crave-cream">
                      <c.icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <h2 className="mt-5 font-display text-2xl font-medium tracking-tight text-crave-cream sm:text-3xl">
                      vs. {c.title}
                    </h2>
                    <p className="mt-3 text-sm font-semibold uppercase tracking-[0.14em] text-crave-muted">
                      The old way
                    </p>
                    <p className="mt-2 text-base leading-relaxed text-crave-muted">{c.oldWay}</p>
                  </div>
                  <div className="rounded-2xl border border-crave-gold/25 bg-crave-gold/5 p-6 md:self-center">
                    <p className="text-sm font-semibold uppercase tracking-[0.14em] text-crave-gold">
                      The Foodie Crave way
                    </p>
                    <p className="mt-2 text-base leading-relaxed text-crave-cream/85">{c.craveWay}</p>
                  </div>
                </div>
              </Reveal>
            ))}

            <Reveal delay={0.15}>
              <div className="mt-4 rounded-3xl border border-crave-gold/40 bg-[#221d19] p-8 text-center md:p-12">
                <h2 className="font-display text-3xl font-medium tracking-tight text-crave-cream">
                  Stop scrolling. Start dining.
                </h2>
                <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-crave-muted">
                  Foodie Crave is in early access — get in now and help shape it before launch.
                </p>
                <p className="mx-auto mt-4 max-w-lg text-sm leading-relaxed text-crave-muted">
                  Every tool above is good at something — review platforms at crowdsourced opinions,
                  map apps at directions, notes at quick captures. Foodie Crave focuses on the
                  personal part: your saved places, your visits, your dining history, and the people
                  you choose to share them with.
                </p>
                <Link
                  to="/#get-started"
                  data-testid="compare-cta-button"
                  className="mt-8 inline-flex items-center gap-2.5 rounded-full bg-crave-gold px-7 py-3.5 text-sm font-bold text-crave-bg transition-colors duration-300 hover:bg-crave-cream"
                >
                  <Rocket className="h-5 w-5" aria-hidden="true" />
                  Get Early Access
                </Link>
              </div>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
